import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { useList, errMsg, dayName, DAYS, mediaUrl } from "@/lib/api";
import ResourcePage from "@/components/ResourcePage";
import { PageHeader, StatusBadge, Spinner, EmptyState, Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Link2, Clock, Save, Plus, Trash2, GraduationCap, Wrench,
         Pencil, PauseCircle, PlayCircle, UsersRound, Mail } from "lucide-react";

export function ParentsPage() {
  const navigate = useNavigate();
  const [emailConflict, setEmailConflict] = useState(null);   // { existing_parent_id, existing_parent_name, attempted }

  const catchCreateError = (e, values) => {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 409 && d && d.code === "parent_email_exists") {
      setEmailConflict({ ...d, attempted: values });
      return true;   // suppress the generic error toast
    }
    return false;
  };

  return (
    <>
      <ResourcePage
        testId="parents-page"
        title="Parents"
        description="Each parent gets a portal login (default password: Parent@123). Multiple parents (mother, father, guardian) can be linked to the same student — just create a fresh parent for each and link the shared child."
        endpoint="/parents"
        createLabel="Add parent"
        searchKeys={["full_name", "email", "phone", "children_names"]}
        rowLink={(r) => `/admin/parents/${r.id}`}
        catchCreateError={catchCreateError}
        columns={[
          { key: "full_name", label: "Name", render: (r) => <span className="font-medium">{r.full_name}</span> },
          { key: "phone", label: "Phone" },
          { key: "email", label: "Email" },
          { key: "children_names", label: "Children" },
        ]}
        fields={[
          { name: "full_name", label: "Full name", required: true },
          { name: "email", label: "Email (portal login)", type: "email", required: true },
          { name: "phone", label: "Phone" },
          { name: "address", label: "Address", type: "textarea" },
          { name: "password", label: "Portal password (optional)", placeholder: "Default: Parent@123" },
        ]}
      />
      <Dialog open={!!emailConflict} onOpenChange={(v) => !v && setEmailConflict(null)}>
        <DialogContent className="max-w-md" data-testid="parent-email-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-amber-600" />
              Email already in use
            </DialogTitle>
            <DialogDescription>
              The email <span className="font-mono">{emailConflict?.attempted?.email}</span> is already linked to another account.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
            <p><span className="text-muted-foreground text-xs">Existing account</span></p>
            <p className="font-semibold">{emailConflict?.existing_parent_name || emailConflict?.attempted?.full_name}</p>
            <p className="text-xs text-muted-foreground">Role: {emailConflict?.role || "parent"}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            If this is the same person (mother, father, or guardian), open the existing parent record and
            link additional children there. To register a second parent with a different login,
            use a distinct email address (e.g. add <span className="font-mono">+father</span> before the @).
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setEmailConflict(null)}
              data-testid="parent-email-conflict-cancel">Cancel</Button>
            {emailConflict?.existing_parent_id && (
              <Button onClick={() => {
                const id = emailConflict.existing_parent_id;
                setEmailConflict(null);
                navigate(`/admin/parents/${id}`);
              }} data-testid="parent-email-conflict-open">
                <UsersRound className="h-4 w-4 mr-1" />Open existing parent
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ParentDetailPage() {
  const { id } = useParams();
  const { data: p, loading, reload } = useList(`/parents/${id}`);
  const { data: students } = useList("/students");
  const [childId, setChildId] = useState("");

  const linkChild = async () => {
    if (!childId) return;
    try {
      await api.post(`/parents/${id}/children`, { student_id: childId, relationship_type: "parent" });
      toast.success("Child linked");
      setChildId("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading || !p) return <Spinner full />;
  return (
    <div data-testid="parent-detail-page">
      <Link to="/admin/parents" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> All parents
      </Link>
      <PageHeader title={p.full_name} description={`${p.phone || ""} · ${p.email}`} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-lg font-semibold mb-3">Children</h3>
          {p.children.length === 0 ? <EmptyState text="No children linked." /> : p.children.map((c) => (
            <div key={c.id} className="flex justify-between items-center py-2 border-b last:border-0 text-sm">
              <div>
                <p className="font-medium">{c.full_name}</p>
                <p className="text-xs text-muted-foreground">{c.current_level || "—"} · <StatusBadge status={c.status} /></p>
              </div>
              <Button asChild variant="ghost" size="sm"><Link to={`/admin/students/${c.id}`}>View</Link></Button>
            </div>
          ))}
          <div className="flex gap-2 mt-4">
            <Select value={childId} onValueChange={setChildId}>
              <SelectTrigger data-testid="link-child-select" className="flex-1">
                <SelectValue placeholder="Link another child..." />
              </SelectTrigger>
              <SelectContent>
                {(students || []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={linkChild} variant="outline" data-testid="link-child-button">
              <Link2 className="h-4 w-4 mr-1" />Link
            </Button>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-lg font-semibold mb-3">Recent invoices</h3>
          {p.invoices.length === 0 ? <EmptyState text="No invoices." /> : p.invoices.map((i) => (
            <div key={i.id} className="flex justify-between items-center py-2 border-b last:border-0 text-sm">
              <div>
                <p className="font-mono text-xs">{i.invoice_number}</p>
                <p className="text-xs text-muted-foreground">{i.student_name} · {i.invoice_month}</p>
              </div>
              <div className="text-right">
                <Money value={i.total_fees_collected} />
                <div><StatusBadge status={i.status} /></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {p.co_parents && p.co_parents.length > 0 && (
        <div className="mt-6 rounded-lg border bg-card p-5" data-testid="co-parents-card">
          <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
            <UsersRound className="h-4 w-4 text-primary" /> Co-parents / co-guardians
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            Other parent portals linked to the same child(ren). Each has their own login.
          </p>
          <div className="grid gap-2 md:grid-cols-2">
            {p.co_parents.map((cp) => (
              <div key={cp.id} className="flex items-center justify-between rounded-md border p-3 text-sm"
                data-testid={`co-parent-${cp.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{cp.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{cp.email}{cp.phone && ` · ${cp.phone}`}</p>
                  <p className="text-[11px] text-primary/80 mt-0.5">Shares: {cp.shared_children.join(", ")}</p>
                </div>
                <Button asChild variant="ghost" size="sm">
                  <Link to={`/admin/parents/${cp.id}`}>Open</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function TeachersPage({ autoOpenCreate = false }) {
  const navigate = useNavigate();
  const [healing, setHealing] = useState(false);
  const runAutoHeal = async () => {
    if (!window.confirm("Auto-heal will:\n• Create portal logins for teachers with email but no login\n• Merge duplicate teacher rows (same name + same phone)\n• Re-point schedules/availability/payroll to the surviving teacher\n\nContinue?")) return;
    setHealing(true);
    try {
      const r = await api.post("/admin/teachers/auto-heal");
      const c = r.data.created_logins?.length || 0;
      const re = r.data.recreated_logins?.length || 0;
      const m = r.data.merged_duplicates?.length || 0;
      toast.success(`Auto-heal done — created ${c}, re-linked ${re}, merged ${m} duplicate(s)`);
      window.location.reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setHealing(false); }
  };
  return (
    <ResourcePage
      testId="teachers-page"
      title="Teachers"
      description="Teacher accounts get portal logins (default password: Teacher@123). Commission rate is used for payroll."
      endpoint="/teachers"
      autoOpenCreate={autoOpenCreate}
      canDelete={true}
      createLabel="Register teacher"
      searchKeys={["full_name", "email", "phone"]}
      rowLink={(r) => `/admin/teachers/${r.id}`}
      afterCreate={(t) => navigate(`/admin/teachers/${t.id}?onboarding=1`)}
      headerExtra={
        <Button variant="outline" size="sm" disabled={healing} onClick={runAutoHeal}
                data-testid="auto-heal-teachers-btn" title="Fix missing logins and merge duplicate teacher rows">
          <Wrench className="h-4 w-4 mr-1" />{healing ? "Healing…" : "Auto-heal logins"}
        </Button>
      }
      columns={[
        { key: "full_name", label: "Name", render: (r) => <span className="font-medium">{r.full_name}</span> },
        { key: "email", label: "Email" },
        { key: "login", label: "Portal login", render: (r) => (
          r.email && r.user_id
            ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs font-medium" data-testid={`teacher-login-ready-${r.id}`}>✓ Ready</span>
            : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-medium" data-testid={`teacher-login-missing-${r.id}`}>⚠ No login — add email</span>
        ) },
        { key: "employment_type", label: "Employment", render: (r) => (r.employment_type || "").replace("_", " ") },
        { key: "commission_rate", label: "Commission", render: (r) => r.commission_rate ? `${Math.round(r.commission_rate * 100)}%` : "—" },
        { key: "status", label: "Status", badge: true },
      ]}
      fields={[
        { name: "profile_photo_url", label: "Profile photo", type: "photo", full: true,
          uploadPath: (v) => `/teachers/${v.id}/photo/upload`,
          deletePath: (v) => `/teachers/${v.id}/photo` },
        { name: "full_name", label: "Full name", required: true },
        { name: "email", label: "Email (portal login)", type: "email", required: true },
        { name: "phone", label: "Phone" },
        { name: "employment_type", label: "Employment type", type: "select", defaultValue: "part_time", options: [
          { value: "full_time", label: "Full time" }, { value: "part_time", label: "Part time" },
          { value: "freelance", label: "Freelance" }] },
        { name: "commission_rate", label: "Commission rate", type: "select", options: [
          { value: "0.52", label: "52%" }, { value: "0.55", label: "55%" },
          { value: "0.58", label: "58%" }, { value: "0.6", label: "60%" }] },
        { name: "status", label: "Status", type: "select", defaultValue: "active", options: [
          { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
        { name: "visible_for_online_booking", label: "Visible on public site", type: "switch", defaultValue: true },
        { name: "bio", label: "Bio", type: "textarea" },
        { name: "password", label: "Portal password (optional)", placeholder: "Default: Teacher@123" },
      ]}
      transform={(payload) => ({
        ...payload,
        commission_rate: payload.commission_rate ? parseFloat(payload.commission_rate) : null,
        visible_for_online_booking: !!payload.visible_for_online_booking,
      })}
    />
  );
}

// Admin editor for a teacher's weekly availability — supports MULTIPLE slots
// per day, each with its own start/end time and (optional) preferred studio/lot.
function TeacherAvailabilityEditor({ teacherId }) {
  const { data: windows, loading, reload } = useList(`/teachers/${teacherId}/availability`);
  const { data: studios } = useList("/studios");
  const [byDay, setByDay] = useState(null); // [{slots: [{start_time, end_time, studio_id}]}]  index 0..6
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (windows === null) return;
    const buckets = DAYS.map(() => []);
    (windows || []).filter((w) => w.is_available).forEach((w) => {
      buckets[w.day_of_week].push({
        start_time: w.start_time, end_time: w.end_time, studio_id: w.studio_id || "",
      });
    });
    buckets.forEach((s) => s.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    setByDay(buckets);
  }, [windows]);

  const addSlot = (d) => {
    const next = [...byDay];
    next[d] = [...next[d], { start_time: "10:00", end_time: "12:00", studio_id: "" }];
    setByDay(next);
  };
  const removeSlot = (d, idx) => {
    const next = [...byDay];
    next[d] = next[d].filter((_, i) => i !== idx);
    setByDay(next);
  };
  const patchSlot = (d, idx, p) => {
    const next = [...byDay];
    next[d] = next[d].map((s, i) => (i === idx ? { ...s, ...p } : s));
    setByDay(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = [];
      byDay.forEach((slots, d) => {
        if (slots.length === 0) {
          payload.push({ day_of_week: d, start_time: "10:00", end_time: "10:01", is_available: false });
        } else {
          slots.forEach((s) => payload.push({
            day_of_week: d, start_time: s.start_time, end_time: s.end_time,
            is_available: true, studio_id: s.studio_id || null,
          }));
        }
      });
      await api.put(`/teachers/${teacherId}/availability`, { windows: payload });
      toast.success("Availability saved — online booking now reflects every slot");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border bg-card p-5 lg:col-span-2" data-testid="admin-teacher-availability">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="text-lg font-semibold">Availability (multiple slots per day)</h3>
        <Button size="sm" onClick={save} disabled={busy || !byDay} data-testid="admin-save-availability">
          <Save className="h-4 w-4 mr-1" />{busy ? "Saving..." : "Save availability"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Add as many time blocks as you need per day (e.g. morning at Studio A, evening at Studio B). Days with no slots are treated as unavailable.
      </p>
      {loading || !byDay ? <Spinner /> : (
        <div className="space-y-3">
          {DAYS.map((name, d) => (
            <div key={name} className={`rounded-lg border p-3 ${byDay[d].length ? "bg-card" : "bg-muted/30"}`}
                 data-testid={`admin-availability-day-${d}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Clock className={`h-4 w-4 ${byDay[d].length ? "text-secondary" : "text-muted-foreground"}`} />
                  <p className="text-sm font-medium">{name}</p>
                  <span className="text-xs text-muted-foreground">
                    {byDay[d].length === 0 ? "Unavailable" : `${byDay[d].length} slot${byDay[d].length > 1 ? "s" : ""}`}
                  </span>
                </div>
                <Button size="sm" variant="outline" onClick={() => addSlot(d)}
                        data-testid={`admin-availability-add-slot-${d}`}>
                  <Plus className="h-3 w-3 mr-1" /> Add slot
                </Button>
              </div>
              {byDay[d].map((slot, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-2 py-1.5 border-t first:border-t-0"
                     data-testid={`admin-availability-slot-${d}-${idx}`}>
                  <Input type="time" className="w-28" value={slot.start_time}
                         onChange={(e) => patchSlot(d, idx, { start_time: e.target.value })}
                         data-testid={`admin-availability-start-${d}-${idx}`} />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input type="time" className="w-28" value={slot.end_time}
                         onChange={(e) => patchSlot(d, idx, { end_time: e.target.value })}
                         data-testid={`admin-availability-end-${d}-${idx}`} />
                  <Select value={slot.studio_id || "_any"}
                          onValueChange={(v) => patchSlot(d, idx, { studio_id: v === "_any" ? "" : v })}>
                    <SelectTrigger className="w-48" data-testid={`admin-availability-studio-${d}-${idx}`}>
                      <SelectValue placeholder="Any studio" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_any">Any studio</SelectItem>
                      {(studios || []).map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" onClick={() => removeSlot(d, idx)}
                          data-testid={`admin-availability-remove-${d}-${idx}`}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Admin/Teacher picker for what courses & levels a teacher can teach.
// Drives smart-matching on the public booking + trial-class flows.
function TeacherTeachingPicker({ teacherId, endpoint }) {
  // endpoint = "/teachers/{id}/teaching" (admin) or "/teacher/teaching" (self)
  const { data: courses } = useList("/courses");
  const { data: teaching, reload } = useList(endpoint);
  const [courseIds, setCourseIds] = useState(new Set());
  const [levelIds, setLevelIds] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!teaching) return;
    setCourseIds(new Set(teaching.course_ids || []));
    setLevelIds(new Set(teaching.level_ids || []));
  }, [teaching]);

  const toggleCourse = (cid, allLevelIds) => {
    const c = new Set(courseIds), l = new Set(levelIds);
    if (c.has(cid)) {
      c.delete(cid);
      allLevelIds.forEach((id) => l.delete(id));
    } else {
      c.add(cid);
      allLevelIds.forEach((id) => l.add(id)); // default: all levels selected
    }
    setCourseIds(c); setLevelIds(l);
  };
  const toggleLevel = (lid, cid) => {
    const c = new Set(courseIds), l = new Set(levelIds);
    if (l.has(lid)) l.delete(lid); else { l.add(lid); c.add(cid); }
    setCourseIds(c); setLevelIds(l);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(endpoint, {
        course_ids: [...courseIds], level_ids: [...levelIds],
      });
      toast.success("Teaching scope saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const visibleCourses = useMemo(
    () => (courses || []).filter((c) => c.status !== "inactive"), [courses]);

  // Group courses under their category headers so admin can see at a glance
  // which courses belong where (matches the public /courses categorisation).
  const groupedByCategory = useMemo(() => {
    const groups = new Map();
    visibleCourses.forEach((c) => {
      const cat = (c.category || "Other").trim() || "Other";
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push(c);
    });
    // Sort each group alphabetically by course name + return as [[cat, courses]]
    return [...groups.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([cat, list]) => [cat, list.sort((a, b) => a.name.localeCompare(b.name))]);
  }, [visibleCourses]);

  return (
    <div className="rounded-lg border bg-card p-5 lg:col-span-2" data-testid="teacher-teaching-picker">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-secondary" />
          <h3 className="text-lg font-semibold">Courses &amp; grades this teacher can teach</h3>
        </div>
        <Button size="sm" onClick={save} disabled={busy} data-testid="save-teaching">
          <Save className="h-4 w-4 mr-1" />{busy ? "Saving..." : "Save"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Tick the courses (and optionally the specific grades within) this teacher is qualified for. Grouped by category. Used by online booking + trial smart-matching.
      </p>
      {!courses ? <Spinner /> : (
        <div className="space-y-5" data-testid="teaching-courses-by-category">
          {groupedByCategory.map(([category, list]) => (
            <div key={category} data-testid={`teaching-category-${category}`}>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-secondary mb-2">
                {category}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.map((c) => {
                  const lvls = c.levels || [];
                  const allLvlIds = lvls.map((l) => l.id);
                  const picked = courseIds.has(c.id);
                  return (
                    <div key={c.id} className={`rounded-lg border p-3 ${picked ? "bg-secondary/5 border-secondary/30" : "bg-card"}`}
                         data-testid={`teaching-course-${c.id}`}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox checked={picked} onCheckedChange={() => toggleCourse(c.id, allLvlIds)}
                                  data-testid={`teaching-course-toggle-${c.id}`} />
                        <span className="font-medium text-sm">{c.name}</span>
                      </label>
                      {picked && lvls.length > 0 && (
                        <div className="mt-2 ml-6 flex flex-wrap gap-x-3 gap-y-1.5">
                          {lvls.map((l) => (
                            <label key={l.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                              <Checkbox checked={levelIds.has(l.id)}
                                        onCheckedChange={() => toggleLevel(l.id, c.id)}
                                        data-testid={`teaching-level-toggle-${l.id}`} />
                              <span>{l.level_name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TeacherDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: t, loading, reload } = useList(`/teachers/${id}`);
  const { data: teaching } = useList(`/teachers/${id}/teaching`);
  const { data: windows } = useList(`/teachers/${id}/availability`);
  const [params] = useSearchParams();
  const isOnboarding = params.get("onboarding") === "1";
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  if (loading || !t) return <Spinner full />;
  const noCourses = teaching && (teaching.course_ids || []).length === 0;
  const noAvailability = windows && (windows || []).filter((w) => w.is_available).length === 0;
  const showBanner = isOnboarding || noCourses || noAvailability;

  const suspend = async () => {
    const next = t.status === "suspended" ? "active" : "suspended";
    const verb = next === "suspended" ? "suspend" : "reactivate";
    if (!window.confirm(`Are you sure you want to ${verb} ${t.full_name}?`)) return;
    setBusy(true);
    try {
      await api.put(`/teachers/${id}`, { ...t, status: next });
      toast.success(`${t.full_name} ${next === "suspended" ? "suspended" : "reactivated"}`);
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(
      `Delete ${t.full_name}? They will be soft-deleted: portal access revoked and ` +
      `hidden from the public site. Past payroll + schedules stay intact for audit.`)) return;
    setBusy(true);
    try {
      await api.delete(`/teachers/${id}`);
      toast.success(`${t.full_name} deleted`);
      navigate("/admin/teachers");
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div data-testid="teacher-detail-page">
      <Link to="/admin/teachers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> All teachers
      </Link>
      <PageHeader title={t.full_name} description={`${t.email || "No portal login"} · ${t.phone || ""}`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={t.status} />
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={busy} data-testid="edit-teacher-btn">
              <Pencil className="h-3.5 w-3.5 mr-1" />Edit
            </Button>
            <Button size="sm" variant="outline" onClick={suspend} disabled={busy} data-testid="suspend-teacher-btn">
              {t.status === "suspended"
                ? <><PlayCircle className="h-3.5 w-3.5 mr-1" />Reactivate</>
                : <><PauseCircle className="h-3.5 w-3.5 mr-1" />Suspend</>}
            </Button>
            <Button size="sm" variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/10" onClick={remove} disabled={busy} data-testid="delete-teacher-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
            </Button>
          </div>
        } />
      {showBanner && (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="teacher-onboarding-banner">
          <p className="text-sm font-semibold text-amber-900 mb-1">
            {isOnboarding ? `✨ ${t.full_name} registered — finish setting them up below` : `Finish setting up ${t.full_name}`}
          </p>
          <ul className="text-xs text-amber-800 space-y-0.5 ml-1">
            {!t.email && <li>• Set an <strong>Email (portal login)</strong> via the Edit button so they can sign in.</li>}
            {noCourses && <li>• Pick the <strong>Courses &amp; grades</strong> this teacher can teach (smart-matching uses these).</li>}
            {noAvailability && <li>• Add their <strong>weekly availability slots</strong> — without this they won&apos;t appear on the public booking page.</li>}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-lg font-semibold mb-3">Profile</h3>
          <p className="text-sm text-muted-foreground mb-3">{t.bio || "No bio."}</p>
          <div className="text-sm space-y-1.5">
            <p><span className="text-muted-foreground">Employment:</span> {(t.employment_type || "").replace("_", " ")}</p>
            <p><span className="text-muted-foreground">Commission rate:</span> {t.commission_setting ? `${Math.round(t.commission_setting.default_commission_rate * 100)}%` : "Default 55%"}</p>
            <p><span className="text-muted-foreground">Statutory:</span>{" "}
              {t.contribution_setting
                ? ["kwsp_eligible", "socso_eligible", "eis_eligible"].filter((k) => t.contribution_setting[k]).map((k) => k.split("_")[0].toUpperCase()).join(", ") || "None"
                : "Not configured"}
            </p>
            <p className="text-xs text-muted-foreground pt-2">Payroll settings are managed in Teacher Payroll → Settings.</p>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-5" data-testid="teacher-courses-panel">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-secondary" />
            Music courses they teach ({(teaching?.labels || []).length})
          </h3>
          {!teaching ? <Spinner /> : (teaching.labels || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No courses tagged yet — pick courses in the panel below to enable public booking + smart matching.</p>
          ) : (
            <ul className="space-y-2" data-testid="teacher-courses-list">
              {teaching.labels.map((lab) => (
                <li key={lab.course_id} className="text-sm border rounded-md p-2.5">
                  <p className="font-medium">{lab.course_name}</p>
                  {(lab.level_names || []).length > 0 && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Grades: {lab.level_names.join(", ")}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-lg font-semibold mb-3">Weekly classes ({t.schedules.length})</h3>
          {t.schedules.length === 0 ? <EmptyState text="No classes assigned." /> : t.schedules.map((c) => (
            <div key={c.id} className="py-2 border-b last:border-0 text-sm">
              <p className="font-medium">{c.student_name} — {c.course_name}</p>
              <p className="text-xs text-muted-foreground">{dayName(c.day_of_week)} {c.start_time}–{c.end_time} · {c.studio_name}</p>
            </div>
          ))}
        </div>
        <TeacherAvailabilityEditor teacherId={id} />
        <TeacherTeachingPicker teacherId={id} endpoint={`/teachers/${id}/teaching`} />
      </div>

      <TeacherEditDialog open={editing} onClose={() => setEditing(false)}
                          teacher={t} onSaved={() => { setEditing(false); reload(); }} />
    </div>
  );
}

// Small modal used by TeacherDetailPage Edit button. Reuses /teachers/{id} PUT.
function TeacherEditDialog({ open, onClose, teacher, onSaved }) {
  const [form, setForm] = useState(teacher);
  const [busy, setBusy] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  useEffect(() => { setForm(teacher); }, [teacher]);
  if (!open) return null;

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5 MB"); e.target.value = ""; return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/teachers/${teacher.id}/photo/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, profile_photo_url: r.data.profile_photo_url });
      toast.success("Photo uploaded");
    } catch (err) { toast.error(errMsg(err)); } finally {
      setUploadingPhoto(false); e.target.value = "";
    }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove this teacher's profile photo?")) return;
    try {
      await api.delete(`/teachers/${teacher.id}/photo`);
      setForm({ ...form, profile_photo_url: null });
      toast.success("Photo removed");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        full_name: form.full_name, email: form.email, phone: form.phone,
        bio: form.bio, employment_type: form.employment_type,
        commission_rate: form.commission_rate
          ? parseFloat(form.commission_rate) : null,
        status: form.status, visible_for_online_booking: !!form.visible_for_online_booking,
      };
      if (form.password) payload.password = form.password;
      await api.put(`/teachers/${teacher.id}`, payload);
      toast.success("Profile updated");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-teacher-dialog">
        <DialogHeader>
          <DialogTitle>Edit teacher profile</DialogTitle>
          <DialogDescription>Update name, login, employment type, commission rate, bio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Profile photo */}
          <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3" data-testid="teacher-photo-block">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border bg-muted">
              {form.profile_photo_url ? (
                <img src={mediaUrl(form.profile_photo_url)}
                     alt={form.full_name || "Teacher"}
                     className="h-full w-full object-cover"
                     data-testid="teacher-photo-preview" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">
                  No photo
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-wrap gap-2">
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden"
                       onChange={uploadPhoto} data-testid="teacher-photo-upload-input"
                       disabled={uploadingPhoto} />
                <span className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs hover:bg-muted/60">
                  {uploadingPhoto ? "Uploading…" : (form.profile_photo_url ? "Replace photo" : "Upload photo")}
                </span>
              </label>
              {form.profile_photo_url && (
                <Button size="sm" variant="outline" onClick={removePhoto}
                        data-testid="teacher-photo-remove-btn"
                        className="text-rose-700 border-rose-200">
                  Remove
                </Button>
              )}
              <p className="w-full text-[11px] text-muted-foreground">
                JPG/PNG/WebP up to 5 MB. Square 400×400 works best.
              </p>
            </div>
          </div>
          <Field label="Full name" v={form.full_name} on={(v) => setForm({ ...form, full_name: v })} testId="edit-full_name" />
          <Field label="Email (portal login)" v={form.email || ""} on={(v) => setForm({ ...form, email: v })} testId="edit-email" />
          <Field label="Phone" v={form.phone || ""} on={(v) => setForm({ ...form, phone: v })} testId="edit-phone" />
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Employment type" v={form.employment_type} on={(v) => setForm({ ...form, employment_type: v })}
              options={[["part_time", "Part time"], ["full_time", "Full time"], ["freelance", "Freelance"]]} testId="edit-employment" />
            <SelectField label="Commission rate" v={form.commission_rate?.toString() || ""} on={(v) => setForm({ ...form, commission_rate: v })}
              options={[["", "—"], ["0.52", "52%"], ["0.55", "55%"], ["0.58", "58%"], ["0.6", "60%"]]} testId="edit-commission" />
          </div>
          <SelectField label="Status" v={form.status} on={(v) => setForm({ ...form, status: v })}
            options={[["active", "Active"], ["suspended", "Suspended"], ["inactive", "Inactive"]]} testId="edit-status" />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!form.visible_for_online_booking}
                    onChange={(e) => setForm({ ...form, visible_for_online_booking: e.target.checked })}
                    data-testid="edit-visible" />
            Visible on public site (Music Instructor list)
          </label>
          <div className="space-y-1.5">
            <Label className="text-xs">Bio</Label>
            <Textarea rows={3} value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                      data-testid="edit-bio" />
          </div>
          <Field label="Reset password (optional)" v={form.password || ""} on={(v) => setForm({ ...form, password: v })}
                  placeholder="Leave blank to keep current" testId="edit-password" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-save-btn">{busy ? "Saving…" : "Save changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, on, testId, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} data-testid={testId} />
    </div>
  );
}
function SelectField({ label, v, on, options, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <select value={v} onChange={(e) => on(e.target.value)}
              className="w-full rounded-md border px-3 py-2 bg-background text-sm" data-testid={testId}>
        {options.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
      </select>
    </div>
  );
}
